"use client";

import { useState } from "react";
import { Plus } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CustomProductionAdvantages() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const components = [{
    id: "resin",
    label: "Raw Resin",
    number: "01",
    name: "Polymer Selection",
    description: "High-grade PE resin pellets sourced for optimal tensile strength. We calibrate the molecular density to ensure every batch meets industrial durability standards.",
    specs: [{
      label: "Material",
      value: "Virgin PE"
    }, {
      label: "Density",
      value: "High-Grade"
    }, {
      label: "Finish",
      value: "Granular"
    }],
    top: "32%",
    left: "82%"
  }, {
    id: "extrusion",
    label: "Extrusion",
    number: "02",
    name: "Film Blowing",
    description: "Precision-controlled bubble extrusion process. We monitor thickness and cooling rates to create uniform film rolls with consistent structural integrity.",
    specs: [{
      label: "Process",
      value: "Blown Film"
    }, {
      label: "Thickness",
      value: "Custom"
    }, {
      label: "Coating",
      value: "Anti-Static"
    }],
    top: "42%",
    left: "48%"
  }, {
    id: "printing",
    label: "Printing",
    number: "03",
    name: "Flexographic Print",
    description: "High-speed flexographic printing systems applying custom logos and branding. We use eco-friendly inks that bond perfectly to the polymer surface.",
    specs: [{
      label: "Method",
      value: "Flexo"
    }, {
      label: "Colors",
      value: "Up to 8"
    }, {
      label: "Setting",
      value: "UV-Cured"
    }],
    top: "55%",
    left: "38%"
  }, {
    id: "sealing",
    label: "Sealing",
    number: "04",
    name: "Thermal Welding",
    description: "Automated heat-sealing lines create leak-proof seams. Our thermal welding technology ensures the bottom of every bag withstands maximum load capacity.",
    specs: [{
      label: "Construction",
      value: "Star-Seal"
    }, {
      label: "Strength",
      value: "Industrial"
    }, {
      label: "Finish",
      value: "Reinforced"
    }],
    top: "68%",
    left: "62%"
  }, {
    id: "packaging",
    label: "Packaging",
    number: "05",
    name: "Bulk Logistics",
    description: "Specialized wholesale packaging for private-label brands. We offer fast turnaround on bulk orders with custom roll counts and palletized shipping for global distribution.",
    specs: [{
      label: "Origin",
      value: "Akyasco Plant"
    }, {
      label: "Capacity",
      value: "Wholesale"
    }, {
      label: "Labeling",
      value: "Private-Label"
    }],
    top: "84%",
    left: "28%"
  }, {
    id: "customization",
    label: "Customization",
    number: "06",
    name: "Customization Options",
    description: "Tailor every aspect of your garbage bag production. From specific dimensions and thickness to custom branding and perforation styles, we build to your exact specs.",
    specs: [{
      label: "Dimensions",
      value: "Custom W/L"
    }, {
      label: "Thickness",
      value: "10-100 Mic"
    }, {
      label: "Branding",
      value: "Print Logos"
    }],
    top: "20%",
    left: "65%"
  }];
  const [activeId, setActiveId] = useState<string>("customization");
  const active = components.find(c => c.id === activeId) ?? components[0];
  return <section data-section-id="5188" id="custom-production-advantages" className="dark bg-background text-foreground relative overflow-hidden py-20 lg:py-32">
      <div ref={ref} className={`mx-auto max-w-[1400px] px-6 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {}
          <div className="relative lg:col-span-7 lg:-ml-6">
            <div className="relative w-full h-[60vh] sm:h-[75vh] lg:h-[90vh] overflow-hidden">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700859/img/akyasco_factory_production.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700859/img/akyasco_factory_production.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700859/img/akyasco_factory_production.png-wvc-sizes" alt="Akyasco industrial plastic manufacturing process" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-background/40" />

              {}
              <div className="hidden lg:block">
                {components.map((c, i) => <button key={c.id} data-index={i} onMouseEnter={() => setActiveId(c.id)} onFocus={() => setActiveId(c.id)} onClick={() => setActiveId(c.id)} aria-label={`View details for ${c.label}`} className="absolute -translate-x-1/2 -translate-y-1/2 group" style={{
                top: c.top,
                left: c.left
              }}>
                    <span className={`absolute inset-0 -m-3 rounded-lg ${activeId === c.id ? "animate-ping bg-primary/30" : "bg-primary/10 animate-pulse"}`} style={{
                  borderRadius: "9999px"
                }} />
                    <span className={`relative block size-3 rounded-lg border transition-all duration-300 ${activeId === c.id ? "bg-primary border-primary scale-125" : "bg-primary/60 border-primary/80 group-hover:scale-110"}`} style={{
                  borderRadius: "9999px"
                }} />
                  </button>)}
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-5 lg:pt-8">
            <div className="font-mono text-xs uppercase tracking-widest text-primary mb-6">
              Engineering / 03
            </div>

            <h2 className="font-default text-5xl sm:text-6xl lg:text-7xl leading-[0.95] text-foreground mb-10">
              Custom <em className="italic font-light">Garbage Bag</em>
              <br />
              Production
            </h2>

            <p className="font-default text-base text-muted-foreground leading-relaxed max-w-md mb-12">
              Akyasco specializes in high-capacity garbage bag manufacturing, offering full customization for wholesale partners. From material density to custom branding, we engineer solutions for every industrial need.
            </p>

            {}
            <div key={active.id} className="relative border border-border p-8 lg:p-10 transition-opacity duration-400 animate-in fade-in">
              <div className="absolute -top-3 left-8 bg-background px-3 font-mono text-[10px] uppercase tracking-[0.25em] text-primary">
                {active.label} / {active.number}
              </div>

              <h3 className="font-default text-3xl lg:text-4xl text-foreground mb-4 leading-tight">
                {active.name}
              </h3>

              <p className="font-default text-sm text-muted-foreground leading-relaxed mb-8">
                {active.description}
              </p>

              {}
              <div className="border border-border/60 bg-card/40 p-5 mb-8">
                <div className="flex items-center justify-between mb-4">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    Fig. {active.number}
                  </span>
                  <Plus className="size-3 text-muted-foreground" />
                </div>
                <div className="relative h-24 flex items-center justify-center">
                  <div className="absolute inset-x-0 top-1/2 h-px bg-border" />
                  <div className="absolute inset-y-0 left-1/2 w-px bg-border" />
                  <div className="relative size-16 border border-primary/60 rounded-lg flex items-center justify-center">
                    <div className="size-8 border border-primary/40 rounded-lg" />
                  </div>
                </div>
              </div>

              {}
              <div className="space-y-3">
                {active.specs.map((s, i) => <div key={s.label} data-index={i} className="flex items-baseline justify-between border-b border-border/50 pb-3 last:border-b-0">
                    <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                      {s.label}
                    </span>
                    <span className="font-mono text-sm tabular-nums text-foreground">
                      {s.value}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>
        </div>

        {}
        <div className="hidden lg:grid grid-cols-6 mt-20 border-t border-border pt-8">
          {components.map((c, i) => <button key={c.id} data-index={i} onMouseEnter={() => setActiveId(c.id)} onFocus={() => setActiveId(c.id)} onClick={() => setActiveId(c.id)} className={`group text-left transition-colors duration-300 ${activeId === c.id ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] mb-2">
                {c.number}
              </div>
              <div className="font-mono text-xs uppercase tracking-widest">
                {c.label}
              </div>
              <div className={`mt-3 h-px transition-all duration-500 ${activeId === c.id ? "bg-primary w-full" : "bg-border w-6 group-hover:w-full group-hover:bg-foreground/40"}`} />
            </button>)}
        </div>

        {}
        <div className="lg:hidden mt-10 -mx-6 px-6 overflow-x-auto">
          <div className="flex gap-3 pb-2 w-max">
            {components.map((c, i) => <button key={c.id} data-index={i} onClick={() => setActiveId(c.id)} className={`shrink-0 border px-4 py-3 text-left transition-colors duration-300 ${activeId === c.id ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground"}`}>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] mb-1">
                  {c.number}
                </div>
                <div className="font-mono text-xs uppercase tracking-widest">
                  {c.label}
                </div>
              </button>)}
          </div>
        </div>
      </div>
    </section>;
}